import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { fetchApplication, updateApplication } from 'actions/applications';
import { connect } from 'react-redux';
import { reduxForm, isDirty } from 'redux-form';
import LoanDetails from './LoanDetails';
import CustomerDetails from './CustomerDetails';
import Show from './Show';
import AlertDialog from 'components/AlertDialog';
import { Link } from 'react-router-dom';

const getSteps = () => (['Loan Details', 'Customer Details', 'Confirmation']);

const getStepContent = (step, application=null) => {
  switch (step) {
    case 0:
      return <LoanDetails />
    case 1:
      return <CustomerDetails />
    case 2:
      return <Show application={application}/>
    default:
      return 'Unknown step';
  }
}

const styles = theme => ({
  root: {
    width: '90%',
  },
  button: {
    marginRight: theme.spacing.unit,
  },
  instructions: {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit,
  },
  content: {
    maxWidth: '400px',
    margin: 'auto'
  },
  btnGroup: {
    marginTop: '30px',
    display: 'flex',
    justifyContent: 'space-evenly'
  },
  success: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  }
});

class Edit extends React.Component {
  componentDidMount = () => {
    this.props.dispatch(fetchApplication(this.props.appNumber));
  };

  state = {
    activeStep: 0,
    skipped: new Set(),
  };

  isStepOptional = step => {
    return false;
  };

  handleNext = () => {
    const { activeStep } = this.state;
    let { skipped } = this.state;
    if (this.isStepSkipped(activeStep)) {
      skipped = new Set(skipped.values());
      skipped.delete(activeStep);
    }
    this.setState({
      activeStep: activeStep + 1,
      skipped,
    });
    this.props.dispatch(updateApplication({appNumber: this.props.appNumber}));
  };

  handleSave = () => {
    this.props.dispatch(updateApplication({appNumber: this.props.appNumber, callback: () => {this.props.history.push('/leads')}}));
  };

  handleSubmit = () => {
    const { activeStep } = this.state;

    this.setState({
      activeStep: activeStep + 1,
    });
    this.props.dispatch(updateApplication({appNumber: this.props.appNumber, updateOrLodge: 'lodge', notificationMsg: 'Application lodged successfully!'}));
  }

  handleBack = () => {
    const { activeStep } = this.state;
    this.setState({
      activeStep: activeStep - 1,
    });
  };

  isStepSkipped(step) {
    return this.state.skipped.has(step);
  }

  backBtn = () => {
    return (
      <Button
        disabled={this.state.activeStep === 0}
        onClick={this.handleBack}
        className={this.props.classes.button}
      >
        Back
      </Button>
    );
  }

  saveBtn = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={this.handleSave}
      >Save</Button>
    );
  }

  nextBtn = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={this.handleNext}
      >Next</Button>
    );
  }

  submitBtn = () => {
    return (
      <AlertDialog
        buttonLabel='Submit'
        variant='raised'
        color='secondary'
        confirmAction={this.handleSubmit}
        title='You are about to submit the application, please confirm.'
        desc='This action will result in lodging the application with the lead details. The application will be reviewed and you will be notified about the progress.'
      />
    );
  }

  confirmationStep = () => (this.state.activeStep === getSteps().length - 1);

  lodged = () => (this.props.application && this.props.application.form && this.props.application.form.lodged);

  btnGroup = () => {
    if (this.confirmationStep()) {
      return (
        <div className={this.props.classes.btnGroup}>
          {this.backBtn()}
          {this.saveBtn()}
          {this.submitBtn()}
        </div>
      );
    } else {
      return (
        <div className={this.props.classes.btnGroup}>
          {this.backBtn()}
          {this.nextBtn()}
          {this.saveBtn()}
        </div>
      );
    }
  }

  renderSuccessView = () => {
    return (
      <div className={this.props.classes.success}>
        <Typography className={this.props.classes.instructions}>
          Thank you for submitting the application, you will be contacted as part of the review process.
        </Typography>
        <Typography className={this.props.classes.instructions}>
          <Link to={`/applications/${this.props.appNumber}`}>
            Click here
          </Link>
          {' '}
          to monitor the application progress.
        </Typography>
      </div>
    );
  }

  renderStepContent = () => {
    const { activeStep } = this.state;
    const steps = getSteps();
    const { classes } = this.props;

    if (activeStep === steps.length) {
      return (
        <div>{this.renderSuccessView()}</div>
      );
    }
    return (
      <div className={classes.content}>
        <div className={classes.instructions}>{getStepContent(activeStep, this.props.application)}</div>
        { this.btnGroup() }
      </div>
    );
  }

  render() {
    const { classes } = this.props;
    const steps = getSteps();
    const { activeStep } = this.state;
    if (!this.props.application) {
      return null;
    } else if (this.lodged() && activeStep === 0) { // readonly lead after lodgement
      return (
        <div>
          <Show application={this.props.application} />
        </div>
      );
    } else if (!this.lodged() && this.confirmationStep()) { // readonly lead on confirmation step
      return (
        <div>
          <Show application={this.props.application} />
          { this.btnGroup() }
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => {
            const props = {};
            const labelProps = {};
            if (this.isStepOptional(index)) {
              labelProps.optional = <Typography variant="caption">Optional</Typography>;
            }
            if (this.isStepSkipped(index)) {
              props.completed = false;
            }
            return (
              <Step key={label} {...props}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        <div>
          { this.renderStepContent() }
        </div>
      </div>
    );
  }
}

Edit.propTypes = {
  classes: PropTypes.object,
};

const mapStateToProps = (state, ownProps) => {
  const application = state.applications && state.applications.application;
  const appNumber = ownProps.match.params.appNumber;
  return {
    initialValues: application && application.form,
    application,
    appNumber
  }
};

const reduxed = reduxForm({ form: 'applicationForm', enableReinitialize: true, dirty: isDirty('applicationForm') })(Edit);
const styled = withStyles(styles)(reduxed)
const connected = connect(mapStateToProps)(styled);
export default connected;


// WEBPACK FOOTER //
// ./src/components/Leads/Edit.js